/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Typography2 } from '@coursera/cds-core';

import LazyImgix from 'bundles/page/components/shared/LazyImgix';

import _t from 'i18n!nls/front-page';

const IMGPARAMS = {
  quality: 40,
};

const MAX_IMAGE_WIDTH = 230;
const MAX_IMAGE_HEIGHT = 230;

const styles = {
  container: css`
    height: 100%;
    border-radius: var(--cds-border-radius-200);
    border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    background: var(--cds-color-neutral-background-primary);
    display: flex;
    flex-direction: column;
    position: relative;
    align-items: center;
  `,
  content: css`
    display: flex;
    flex-direction: column;
    text-wrap: wrap;
    padding: var(--cds-spacing-200) var(--cds-spacing-600) var(--cds-spacing-300);
  `,
  lazyImage: css`
    /* disable conflicting css from LazyImgix */
    > div {
      padding-bottom: 0;
      height: 100%;
    }

    .lazyload-wrapper {
      display: flex;
      justify-content: center;
      flex-direction: column;
    }
  `,
  imageFrame: css`
    padding-top: var(--cds-spacing-300);
  `,
  text: css`
    padding-bottom: var(--cds-spacing-300);
  `,
  name: css`
    font-weight: var(--cds-font-weight-700);
    font-size: var(--cds-font-size-200);
    line-height: var(--cds-line-height-300);
  `,
  quotationMark: css`
    position: absolute;
    top: 265px;
    left: 25px;
  `,
};

type Props = {
  authorName?: string | null;
  text?: string | null;
  authorOrganization?: string | null;
  authorTitle?: string | null;
  imageUrl?: string | null;
};

const removeQuotes = (text: string) => {
  // regexp to remove special double quotes: “ and ”
  return text.replace(/^“|”$/g, '');
};

const SocialProofCardContent = ({ authorName, text, authorOrganization, authorTitle, imageUrl }: Props) => {
  if (!authorName || !text || !imageUrl) {
    return null;
  }

  return (
    <div css={styles.container}>
      <div css={styles.imageFrame}>
        <div css={styles.lazyImage}>
          <LazyImgix
            src={imageUrl}
            alt={_t('Testimonial Image')}
            maxHeight={MAX_IMAGE_HEIGHT}
            maxWidth={MAX_IMAGE_WIDTH}
            imgParams={IMGPARAMS}
          />
        </div>
      </div>
      <Typography2 variant="titleLarge" component="div" css={styles.quotationMark}>
        “
      </Typography2>
      <div css={styles.content}>
        <Typography2 variant="bodyPrimary" component="p" css={styles.text}>
          {removeQuotes(text)}
        </Typography2>

        <Typography2 variant="subtitleLarge" component="p" css={styles.name}>
          {authorName}
        </Typography2>
        {authorOrganization && (
          <Typography2 variant="bodySecondary" component="p" color="supportText">
            {authorOrganization}
          </Typography2>
        )}
        {authorTitle && (
          <Typography2 variant="bodySecondary" component="p" color="supportText">
            {authorTitle}
          </Typography2>
        )}
      </div>
    </div>
  );
};

export default SocialProofCardContent;
